import { apiBlob, apiRequest } from '@/shared/api/client';
import {
  chatSummariesSchema,
  consumerChatSummariesSchema,
  messageSchema,
  messagesSchema,
  type ChatMessage,
  type ChatSummary,
  type ConsumerChatSummary,
} from '@/entities/chat/model';

export const chatApi = {
  list: async (operatorId: string, after?: string): Promise<ChatMessage[]> => {
    const query = after ? `?after=${encodeURIComponent(after)}` : '';
    return messagesSchema.parse(
      await apiRequest<unknown>(`/v1/admin/chats/${operatorId}/messages${query}`),
    );
  },
  send: async (operatorId: string, text: string): Promise<ChatMessage> =>
    messageSchema.parse(
      await apiRequest<unknown>(`/v1/admin/chats/${operatorId}/messages`, {
        method: 'POST',
        json: { text },
      }),
    ),

  /* Send a message with image / voice attachments (multipart). Optional
     caption `text` rides along; the backend allows files-only messages. */
  sendMedia: async (operatorId: string, text: string, files: File[]): Promise<ChatMessage> => {
    const form = new FormData();
    const caption = text.trim();
    if (caption) form.append('text', caption);
    for (const file of files) form.append('file', file, file.name);
    return messageSchema.parse(
      await apiRequest<unknown>(`/v1/admin/chats/${operatorId}/messages/media`, {
        method: 'POST',
        form,
      }),
    );
  },

  /* Stream an attachment's bytes (authorized) for <img>/<audio> object URLs. */
  attachmentBlob: (attachmentId: string): Promise<Blob> =>
    apiBlob(`/v1/admin/chat-attachments/${attachmentId}`),

  /* Dispatcher inbox — unread counts per operator chat, newest first. */
  inbox: async (): Promise<ChatSummary[]> =>
    chatSummariesSchema.parse(await apiRequest<unknown>('/v1/admin/chats')),

  /* Mark this operator's messages read for the dispatcher pool. */
  markRead: (operatorId: string): Promise<{ marked: number }> =>
    apiRequest<{ marked: number }>(`/v1/admin/chats/${operatorId}/read`, {
      method: 'POST',
      json: {},
    }),

  /* Ephemeral "dispatcher is typing" ping to the operator (204, no body). */
  typing: (operatorId: string): Promise<void> =>
    apiRequest<void>(`/v1/admin/chats/${operatorId}/typing`, { method: 'POST', json: {} }),
};

/* Consumer-support sibling of `chatApi` (B12) — same shape, keyed on `consumer_id`, hitting the
   parallel `/v1/admin/consumer-chats` route family (never merged into `chatApi`, so the
   already-shipped operator inbox is untouched). */
export const consumerChatApi = {
  list: async (consumerId: string, after?: string): Promise<ChatMessage[]> => {
    const query = after ? `?after=${encodeURIComponent(after)}` : '';
    return messagesSchema.parse(
      await apiRequest<unknown>(`/v1/admin/consumer-chats/${consumerId}/messages${query}`),
    );
  },
  send: async (consumerId: string, text: string): Promise<ChatMessage> =>
    messageSchema.parse(
      await apiRequest<unknown>(`/v1/admin/consumer-chats/${consumerId}/messages`, {
        method: 'POST',
        json: { text },
      }),
    ),

  sendMedia: async (consumerId: string, text: string, files: File[]): Promise<ChatMessage> => {
    const form = new FormData();
    const caption = text.trim();
    if (caption) form.append('text', caption);
    for (const file of files) form.append('file', file, file.name);
    return messageSchema.parse(
      await apiRequest<unknown>(`/v1/admin/consumer-chats/${consumerId}/messages/media`, {
        method: 'POST',
        form,
      }),
    );
  },

  attachmentBlob: (attachmentId: string): Promise<Blob> =>
    apiBlob(`/v1/admin/consumer-chat-attachments/${attachmentId}`),

  /* Support inbox — unread counts per consumer chat, newest first. */
  inbox: async (): Promise<ConsumerChatSummary[]> =>
    consumerChatSummariesSchema.parse(await apiRequest<unknown>('/v1/admin/consumer-chats')),

  markRead: (consumerId: string): Promise<{ marked: number }> =>
    apiRequest<{ marked: number }>(`/v1/admin/consumer-chats/${consumerId}/read`, {
      method: 'POST',
      json: {},
    }),

  typing: (consumerId: string): Promise<void> =>
    apiRequest<void>(`/v1/admin/consumer-chats/${consumerId}/typing`, {
      method: 'POST',
      json: {},
    }),
};
